import {
  FlowConnection,
  FlowNode,
  FlowSticker,
  WorkflowConnectionExport,
} from 'src/app/shared/flow-chart/flow-chart.model';

export namespace WorkflowModel {
  export interface NodePort {
    id: string;
    name?: string;
  }

  export interface GraphUiDataDto {
    nodes: FlowNode[];
    connections: FlowConnection[];
    stickers?: FlowSticker[],
  }

  export interface GetGraphResponse {
    _id: string;
    orgId: string;
    graphId: string;
    preview: PreviewGraphSnapshot;
    published?: PublishedGraphSnapshot;
    publishing?: { publishVersionId: string; startedAt: number };
    latestRunId?: string;
    createdAt: number;
    updatedAt: number;
  }

  export interface PreviewGraphSnapshot {
    version: number;
    previewVersionId: string;
    name: string;
    nodes: GraphNode[];
    connections: GraphConnection[];
    uiData: GraphUiDataDto;
    validations: { invalidUiNodes: { message: string; uiId: string }[] };
  }

  export interface PublishedGraphSnapshot {
    publishVersionId: string;
    publishedAt?: number;
    name: string;
    nodes: GraphNode[];
    connections: GraphConnection[];
    uiData: GraphUiDataDto;
    validations: { invalidUiNodes: { message: string; uiId: string }[] };
  }

  export interface GraphNode {
    nodeId: string;
    type: string;
    name: string;
    uiId: string; // UI node id
  }
  export interface GraphConnection {
    kind: 'main' | 'error' | 'signal';
    type: string; // node type
    fromNode: string;
    fromPort: number;
    toNode: string;
    toPort: number;
  }

  export interface WorkflowDocument {
    name: string;
    graphId: string;
    tags?: string[];
    preview: {
      name: string;
    }
    published: {
      publishVersionId: string;
      publishedAt: string;
      name: string;
      nodes: GraphNode[];
    } | null;
    createdAt: number;
    updatedAt: number;
    enabled?: boolean;
  }

  export interface InitGraphDto {
    orgId: string;
    name?: string;
  }

  export interface UpdateGraphMetadataDto {
    name: string;
    uiData?: GraphUiDataDto;
  }

  export interface ConditionOptionsDto {
    validations?: Validation[];
  }

  export interface Validation {
    path: string;
    op: Operators;
    value?: string | number;
  }

  export enum Operators {
    Equal = '===',
    NotEqual = '!==',
    NotEmpty = 'not_empty',
    GreaterThan = 'gt',
    GreaterThanOrEqual = 'gte',
    LessThan = 'lt',
    LessThanOrEqual = 'lte',
  }

  export enum HttpMethods {
    POST = 'POST',
    GET = 'GET',
  }

  export interface AddNodeToGraphDto {
    nodeType: string;
    name: string;
    uiNodeId: string;
    options?: unknown;
  }

  export interface NodeConfigDocument {
    _id: string;
    orgId: string;
    graphId: string;
    nodeId: string;
    type: string;
    preview: PreviewNodeSnapshot;
    published?: PublishedNodeSnapshot;
    createdAt?: number;
    updatedAt?: number;
  }

  export interface PreviewNodeSnapshot {
    previewVersionId: string;
    options: Record<string, any>;
  }
  export interface PublishedNodeSnapshot {
    publishVersionId: string;
    publishedAt?: number;
    options: Record<string, any>;
  }

  export interface UpdateNodeOptionsRequestDto {
    options?: unknown;
    name: string;
  }

  export interface TriggerWebhookOptionsDto {
    webhookId: string;
  }

  export interface RunGraphWorkflowDto {
    startNodeId: string;
    input: Record<string, unknown>;
  }

  export interface UpdateConnectionsDto {
    connections: WorkflowConnectionExport[];
    uiData?: GraphUiDataDto;
  }

  export enum RunStatus {
    Queued = 'queued',
    Running = 'running',
    Paused = 'paused',
    Completed = 'completed',
    Failed = 'failed',
    Canceled = 'canceled',
    Timed_out = 'timed_out',
    Terminated = 'terminated',
  }

  export interface ListRunsQuery {
    status?: string;
    from?: number;
    to?: number;
    limit?: number;
  }

  export interface WorkflowRunSummaryItem {
    orgId?: string;
    graphId?: string;
    workflowId: string;
    runId: string;
    status?: RunStatus;
    startedAt?: number;
    endedAt?: number;
    durationMs?: number;
    currentNode?: { nodeId?: string; status?: string; startedAt?: number };
    totalNodesExecuted?: number;
    totalNodeFailures?: number;
    totalRetries?: number;
    output?: Record<string, unknown>;
  }

  export interface CancelRunResponse {
    orgId: string;
    graphId: string;
    runId: string;
    accepted: boolean;
    source: 'temporal';
  }

  export const RunStatusMap = {
    [RunStatus.Queued]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.QUEUED',
    [RunStatus.Running]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.RUNNING',
    [RunStatus.Paused]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.PAUSED',
    [RunStatus.Completed]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.COMPLETED',
    [RunStatus.Failed]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.FAILED',
    [RunStatus.Canceled]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.CANCELED',
    [RunStatus.Timed_out]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.TIMED_OUT',
    [RunStatus.Terminated]: 'FLOW_CHART.RUN_LIST_TABLE.RUN_STATUS.TERMINATED',
  }

  export enum StepStatus {
    Started = 'started',
    Waiting = 'waiting',
    Completed = 'completed',
    Failed = 'failed',
    Timed_out = 'timed_out',
    Skipped = 'skipped',
    Canceled = 'canceled',
  }

  export const StepStatusMap = {
    [StepStatus.Started]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.STARTED',
    [StepStatus.Waiting]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.WAITING',
    [StepStatus.Completed]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.COMPLETED',
    [StepStatus.Failed]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.FAILED',
    [StepStatus.Timed_out]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.TIMED_OUT',
    [StepStatus.Skipped]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.SKIPPED',
    [StepStatus.Canceled]: 'FLOW_CHART.RUN_STEPS_TABLE.STEP_STATUS.CANCELED',
  }

  export enum NodeExecutionKind {
    Node_execute = 'node_execute',
    Wait_signal = 'wait_signal',
    Wait_timeout = 'wait_timeout',
  }

  export interface WorkflowRunNodeStepRef {
    nodeId: string;
    nodeType: string;
    stepId: string;
    latestState: StepStatus;
    latestStepSeq: number;
    executionKind?: NodeExecutionKind;
  }

  export interface WorkflowRunStepMetrics {
    startedAt?: number;
    endedAt?: number;
    durationMs?: number;
    waitStartedAt?: number;
    waitEndedAt?: number;
    waitDurationMs?: number;
    attempt?: number;
    maxAttempts?: number;
    failureCount?: number;
    retryCount?: number;
    nextRetryAt?: number;
  }
  export interface WorkflowRunStepView {
    stepId: string;
    nodeId: string;
    nodeType: string;
    stepSeq: number;
    state: StepStatus;
    executionKind?: NodeExecutionKind;
    metrics: WorkflowRunStepMetrics;
    input?: unknown;
    output?: unknown;
    error?: unknown;
  }
  export interface GetRunStepsResponse {
    orgId: string;
    graphId: string;
    runId: string;
    view: 'summary' | 'full';
    stepsByNodeId: Record<string, WorkflowRunNodeStepRef>;
    stepsById?: Record<string, WorkflowRunStepView>;
  }

  export enum WorkflowNodeTypes {
    Webhook = 'triggerWebhook',
    AlertTrigger = 'alertTrigger',
    WaitWebhook = 'waitWebhook',
    WaitAlert = 'waitAlert',
    HttpRequest = 'httpRequest',
    Condition = 'condition',
    Echo = 'echo',
    SendSms = 'sendSms',
    SendEmail = 'sendEmail',
    WaitSms = 'waitSmsAck',
    WaitEmail = 'waitEmailAck',
    Wait = 'wait',
    MobilePush = 'sendMobilePush',
    WaitMobilePush = 'waitMobilePushAck',
    Video = 'getVideo',
    GetImage = 'getImage',
    SetFps = 'setFps',
    Trim = 'trim',
    Crop = 'crop',
    Resize = 'resize',
    AI = 'ai',
    ManualTrigger = 'manualTrigger',
    ScheduleTrigger = 'scheduleTrigger',
    JsCode = 'jsCode',
  }

  export const NodeTypesMap = {
    [WorkflowNodeTypes.Webhook]: 'FLOW_CHART.NODES.WEBHOOK.NAME',
    [WorkflowNodeTypes.AlertTrigger]: 'FLOW_CHART.NODES.ALERTTRIGGER.NAME',
    [WorkflowNodeTypes.WaitWebhook]: 'FLOW_CHART.NODES.WAITWEBHOOK.NAME',
    [WorkflowNodeTypes.WaitAlert]: 'FLOW_CHART.NODES.WAITALERT.NAME',
    [WorkflowNodeTypes.HttpRequest]: 'FLOW_CHART.NODES.HTTPREQ.NAME',
    [WorkflowNodeTypes.Condition]: 'FLOW_CHART.NODES.CONDITION.NAME',
    [WorkflowNodeTypes.Echo]: 'FLOW_CHART.NODES.ECHO.NAME',
    [WorkflowNodeTypes.SendSms]: 'FLOW_CHART.NODES.SEND_SMS.NAME',
    [WorkflowNodeTypes.SendEmail]: 'FLOW_CHART.NODES.SEND_EMAIL.NAME',
    [WorkflowNodeTypes.WaitSms]: 'FLOW_CHART.NODES.WAIT_SMS.NAME',
    [WorkflowNodeTypes.WaitEmail]: 'FLOW_CHART.NODES.WAIT_EMAIL.NAME',
    [WorkflowNodeTypes.Wait]: 'FLOW_CHART.NODES.WAIT.NAME',
    [WorkflowNodeTypes.MobilePush]: 'FLOW_CHART.NODES.MOBILE_PUSH.NAME',
    [WorkflowNodeTypes.WaitMobilePush]: 'FLOW_CHART.NODES.WAIT_MOBILE_PUSH.NAME',
  }

  export enum AiModels {
    GPT_5 = 'gpt_5',
    Gemini_3 = 'gemini_3',
    Qwen3_VL = 'qwen3_vl',
    Cosmos_Reason = 'cosmos_reason',
    VIA_1 = 'via_1',
  }

  export const AiModelsMap = {
    [AiModels.VIA_1]: "VIA-1",
    [AiModels.GPT_5]: 'GPT 5',
    [AiModels.Gemini_3]: 'Gemini 3',
    [AiModels.Qwen3_VL]: 'FQwen3 VL',
    [AiModels.Cosmos_Reason]: 'Cosmos Reason',
  }

  export enum ScheduleIntervals {
    Seconds = 'seconds',
    Minutes = 'minutes',
    Hours = 'hours',
    Days = 'days',
    Weeks = 'weeks',
    Custom = 'custom',
  }

  export const ScheduleIntervalsMap = {
    [ScheduleIntervals.Seconds]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.INTERVALS.SEC",
    [ScheduleIntervals.Minutes]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.INTERVALS.MIN",
    [ScheduleIntervals.Hours]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.INTERVALS.HOURS",
    [ScheduleIntervals.Days]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.INTERVALS.DAYS",
    [ScheduleIntervals.Weeks]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.INTERVALS.WEEKS",
    [ScheduleIntervals.Custom]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.INTERVALS.CUSTOM",
  }

  export enum AtHours {
    Midnight = '00',
    am_1 = '01',
    am_2 = '02',
    am_3 = '03',
    am_4 = '04',
    am_5 = '05',
    am_6 = '06',
    am_7 = '07',
    am_8 = '08',
    am_9 = '09',
    am_10 = '10',
    am_11 = '11',
    Noon = '12',
    pm_1 = '13',
    pm_2 = '14',
    pm_3 = '15',
    pm_4 = '16',
    pm_5 = '17',
    pm_6 = '18',
    pm_7 = '19',
    pm_8 = '20',
    pm_9 = '21',
    pm_10 = '22',
    pm_11 = '23',
  }

  export const AtHoursMap: Record<AtHours, string> = {
    [AtHours.Midnight]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.MIDNIGHT",
    [AtHours.am_1]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_1",
    [AtHours.am_2]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_2",
    [AtHours.am_3]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_3",
    [AtHours.am_4]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_4",
    [AtHours.am_5]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_5",
    [AtHours.am_6]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_6",
    [AtHours.am_7]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_7",
    [AtHours.am_8]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_8",
    [AtHours.am_9]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_9",
    [AtHours.am_10]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_10",
    [AtHours.am_11]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.AM_11",
    [AtHours.Noon]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.NOON",
    [AtHours.pm_1]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_1",
    [AtHours.pm_2]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_2",
    [AtHours.pm_3]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_3",
    [AtHours.pm_4]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_4",
    [AtHours.pm_5]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_5",
    [AtHours.pm_6]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_6",
    [AtHours.pm_7]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_7",
    [AtHours.pm_8]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_8",
    [AtHours.pm_9]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_9",
    [AtHours.pm_10]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_10",
    [AtHours.pm_11]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.HOURS.PM_11",
  };

  export enum Days {
    Monday = 'MONDAY',
    Tuesday = 'TUESDAY',
    Wednesday = 'WEDNESDAY',
    Thursday = 'THURSDAY',
    Friday = 'FRIDAY',
    Saturday = 'SATURDAY',
    Sunday = 'SUNDAY',
  }

  export const DaysMap: Record<Days, string> = {
    [Days.Monday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.MONDAY",
    [Days.Tuesday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.TUESDAY",
    [Days.Wednesday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.WEDNESDAY",
    [Days.Thursday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.THURSDAY",
    [Days.Friday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.FRIDAY",
    [Days.Saturday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.SATURDAY",
    [Days.Sunday]: "FLOW_CHART.NODES.SCHEDULE_TRIGGER.DAYS.SUNDAY",
  };
}
